import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, ChevronLeft, X, Phone } from "lucide-react";
import Pop from "../components/PopupForm";
import api from "../services/api";
import {useNavigate} from "react-router-dom";
/* ---------------- HELPERS ---------------- */

function formatDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
}

function getReadTime(content) {
  if (!content) return "5 min read";
  const words = content.split(/\s+/).length;
  const minutes = Math.max(1, Math.round(words / 200));
  return `${minutes} min read`;
}

/* ---------------- MAIN COMPONENT ---------------- */

export default function BlogsSection() {
  const navigate = useNavigate();
  const [animateOnce, setAnimateOnce] = useState(false);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  {/* const [selectedBlog, setSelectedBlog] = useState(null); */}

  useEffect(() => {
  const fetchBlogs = async () => {
    try {
      const res = await api.get(
        `${process.env.REACT_APP_BACKEND_URL}/posts`
      );

      const posts =
        res.data?.posts ||
        res.data?.data ||
        (Array.isArray(res.data) ? res.data : []);

      const sorted = posts
        .filter((post) => post?.slug)
        .sort(
          (a, b) =>
            new Date(b.createdAt) - new Date(a.createdAt)
        )
        .slice(0, 3);

      setBlogs(sorted);
    } catch (err) {
      console.error(err);
      setError("Failed to load blog articles.");
    } finally {
      setLoading(false);
    }
  };

  fetchBlogs();
}, []);

  return (
    <>
      <section
        className="relative py-24 sm:py-28 md:py-32 bg-gradient-to-b from-white to-gray-50"
        onMouseEnter={() => !animateOnce && setAnimateOnce(true)}
        onTouchStart={() => !animateOnce && setAnimateOnce(true)}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* HEADER */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={animateOnce ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.7 }}
            className="flex flex-col md:flex-row md:items-end justify-between mb-14"
          >
            <div>
              <span className="inline-block px-4 py-2 bg-[#253E90]/5 rounded-full text-xs text-[#253E90] uppercase tracking-wider mb-5">
                Latest Insights
              </span>
              <h2 className="text-4xl font-bold text-gray-900">
                From Our{" "}
                <span className="bg-gradient-to-r from-[#253E90] to-blue-500 bg-clip-text text-transparent">
                  Blog
                </span>
              </h2>
            </div>
          </motion.div>

          {/* GRID */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">

            {loading && <p>Loading...</p>}

            {error && (
              <div className="col-span-3 text-center text-red-500">
                {error}
              </div>
            )}

            {!loading &&
              !error &&
              blogs.map((blog, idx) => (
                <motion.article
                  key={blog._id}
                  initial={{ opacity: 0, scale: 0.92 }}
                  animate={animateOnce ? { opacity: 1, scale: 1 } : {}}
                  transition={{ delay: idx * 0.15 }}
                >
                  <div
                    onClick={() => navigate(`/blogs/${blog.slug}`)}
                    className="group block cursor-pointer"
                  >
                    <div className="relative bg-white rounded-3xl overflow-hidden border border-[#253E90]/10 hover:shadow-xl transition-all">

                      <div className="relative h-64 overflow-hidden">
                        <img
                          src={blog.featuredImage?.url}
                          alt={blog.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                      </div>

                      <div className="p-6">
                        <div className="flex items-center gap-4 text-gray-400 text-sm mb-4">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(blog.createdAt)}
                          </span>
                          <span>
                            {getReadTime(blog.excerpt || blog.description)}
                          </span>
                        </div>

                        <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#253E90]">
                          {blog.title}
                        </h3>

                        <p className="text-sm text-gray-500 line-clamp-2 mb-4">
                          {blog.excerpt || blog.description}
                        </p>

                        <div className="flex items-center gap-2 text-[#253E90]">
                          <span className="text-sm uppercase tracking-wider">
                            Read Article
                          </span>
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.article>
              ))}
          </div>
        </div>
      </section>

      {/* {selectedBlog && (
        <DetailOverlay
          blog={selectedBlog}
          onClose={() => setSelectedBlog(null)}
        />
      )} */}
    </>
  );
}

/* ---------------- OVERLAY COMPONENT ---------------- */

function DetailOverlay({ blog, onClose }) {
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  return (
    <div className="fixed top-20 left-0 right-0 bottom-0 z-[100] bg-white overflow-auto">

      <div className="sticky top-0 z-50 bg-white border-b px-6 py-4 flex items-center justify-between">
  <button
    onClick={onClose}
    className="flex items-center gap-2 text-[#253E90] font-semibold hover:opacity-70 transition"
  >
    <ChevronLeft size={22} />
    Back to Blogs
  </button>
</div>

      <div className="relative h-[60vh]">
        <img
          src={blog.featuredImage?.url}
          alt={blog.title}
          className="w-full h-full object-cover"
        />
      </div>

      <div className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-5xl font-black mb-6">
          {blog.title}
        </h1>

        <div
          className="prose max-w-none"
          dangerouslySetInnerHTML={{
            __html: blog.content || "<p>No content available.</p>",
          }}
        />

        {/* CTA */}
        <div className="mt-24 bg-gradient-to-r from-[#253E90] to-blue-600 text-white rounded-3xl p-12 text-center shadow-xl">
  <h3 className="text-3xl font-bold mb-4">
    Need Expert Guidance?
  </h3>

  <p className="opacity-90 mb-8">
    Book a free door-step counselling session with our experts.
  </p>

  <button
    onClick={() => setShowForm(true)}
    className="bg-white text-[#253E90] px-8 py-4 rounded-xl font-bold hover:scale-105 transition"
  >
    <Phone className="inline w-4 h-4 mr-2" />
    Book Counselling
  </button>
</div>

        <Pop show={showForm} onClose={() => setShowForm(false)} />
      </div>
    </div>
  );
}